import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import classnames from 'classnames';
import { memoize, map, assign, uniqueId } from 'lodash-es';
import { useState, useMemo, useCallback, useRef } from 'react';
import ReactPlayer from 'react-player/lazy';
import { useIsMobile } from '../common/use-is-mobile';
import Modal from "react-modal";
import chevronLeft from '../../images/chevron-left.svg';
import chevronRight from '../../images/chevron-right.svg';
import closeIcon from '../../images/close-icon.svg';
export function VideoSlider(props) {
    const { videos, currentSlideIndex, updateCurrentSlideIndex } = props;
    const [modalIsOpen, setIsOpen] = useState(false);
    const componentId = useMemo(() => uniqueId('video-slider'), []);
    /* Handle resize logic */
    const isMobile = useIsMobile(1024);
    /* Handle change video slide */
    const handleChangeSlide = useCallback(memoize((videoIndex) => () => {
        updateCurrentSlideIndex(videoIndex);
    }), []);
    const previousSlide = useCallback(() => {
        updateCurrentSlideIndex(Math.max(0, currentSlideIndex - 1));
    }, [videos, currentSlideIndex, updateCurrentSlideIndex]);
    const nextSlide = useCallback(() => {
        updateCurrentSlideIndex(Math.min(videos.length - 1, currentSlideIndex + 1));
    }, [videos, currentSlideIndex, updateCurrentSlideIndex]);
    /*
      Style to translate the slider
      on mobile
    */
    const slideContainerRef = useRef(null);
    const mobileStyle = useMemo(() => {
        if (!isMobile || !slideContainerRef.current) {
            return {
                transform: 'translateX(0px)',
            };
        }
        const slideWidth = slideContainerRef.current.offsetWidth;
        return {
            transform: `translateX(${-currentSlideIndex * slideWidth}px)`,
        };
    }, [isMobile, slideContainerRef, currentSlideIndex]);
    /* Handle touch events */
    const [touchState, setTouchState] = useState({
        touchDown: false,
        touchMove: false,
        direction: 0,
        previousPageX: 0,
    });
    const handleTouchStart = useCallback(() => {
        setTouchState(assign({}, touchState, { touchDown: true }));
    }, [setTouchState, touchState]);
    const handleTouchMove = useCallback((event) => {
        if (!touchState.touchDown) {
            return;
        }
        const pageX = event.nativeEvent.touches[0].pageX;
        setTouchState({
            touchDown: true,
            touchMove: true,
            direction: pageX < touchState.previousPageX ? 1 : -1,
            previousPageX: pageX,
        });
    }, [touchState, setTouchState]);
    const handleTouchEnd = useCallback(() => {
        if (!touchState.touchDown || !touchState.touchMove) {
            return;
        }
        const nextIndex = Math.min(Math.max(currentSlideIndex + touchState.direction, 0), videos.length - 1);
        setTouchState({
            touchDown: false,
            touchMove: false,
            direction: 0,
            previousPageX: 0,
        });
        updateCurrentSlideIndex(nextIndex);
    }, [touchState, currentSlideIndex, videos, updateCurrentSlideIndex]);
    /* Select slide events */
    const selectSlideOnClick = useCallback(memoize((slideIndex) => () => {
        if (currentSlideIndex === slideIndex) {
            return;
        }
        updateCurrentSlideIndex(slideIndex);
    }), [currentSlideIndex, updateCurrentSlideIndex]);
    /* Open/Close modal events */
    const openModal = () => setIsOpen(true);
    const closeModal = () => setIsOpen(false);
    return (_jsxs("div", { id: componentId, className: "video-slider", "aria-roledescription": "carousel", "aria-label": "Video Slider", children: [_jsxs("div", { id: `${componentId}-slides`, ref: slideContainerRef, className: "video-slider-slides", style: mobileStyle, onTouchStart: isMobile ? handleTouchStart : undefined, onTouchMove: isMobile ? handleTouchMove : undefined, onTouchEnd: isMobile ? handleTouchEnd : undefined, "aria-live": "off", children: [map(videos, (video, index) => (_jsx("div", { className: classnames('video-slider-slide', { current: index === currentSlideIndex }), onClick: openModal, role: "group", "aria-roledescription": "slide", "aria-label": `Slide ${index + 1} of ${videos.length}`, children: _jsx("img", { className: "video-slider-thumbnail", src: video.poster, alt: "" }) }, index))), _jsxs(Modal, { isOpen: modalIsOpen, onRequestClose: closeModal, contentLabel: "Testing modal", overlayClassName: "modal-overlay", className: "modal-content", children: [_jsx("button", { className: "close-player-trigger", onClick: closeModal, "aria-label": "Close the video player modal", children: _jsx("img", { src: closeIcon, alt: "" }) }), _jsx(ReactPlayer, { url: props.videos[currentSlideIndex].src, controls: true, config: {
                                    wistia: {
                                        options: {
                                            fitStrategy: 'cover',
                                            endVideoBehavior: 'loop',
                                        }
                                    }
                                }, className: 'ReactModal__Player', loop: true, playing: true, muted: false })] })] }), _jsxs("div", { className: "video-slider-arrows", children: [_jsx("button", { onClick: previousSlide, "aria-controls": `${componentId}-slides`, "aria-label": "Previous Slide", children: _jsx("img", { src: chevronLeft, alt: "" }) }), _jsx("button", { onClick: nextSlide, "aria-controls": `${componentId}-slides`, "aria-label": "Next Slide", children: _jsx("img", { src: chevronRight, alt: "" }) })] }), _jsx("div", { className: "video-slider-dots", children: map(videos, (videos, index) => (_jsx("button", { className: classnames({ current: index === currentSlideIndex }), onClick: isMobile ? handleChangeSlide(index) : undefined, "aria-controls": `${componentId}-slides`, "aria-label": `Go to slide ${index + 1}` }, index))) })] }));
}
